import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, RefreshCw, Pencil, X, Bell, BellOff } from 'lucide-react';
import apiService from '../services/api';
import { formatDate } from '../utils/dateUtils';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

// Bucket a statement by its days_until_due into a visual status.
export const statusFor = (daysUntilDue) => {
  if (daysUntilDue == null) return { text: 'No due date', tone: 'gray' };
  if (daysUntilDue < 0) return { text: `Overdue ${-daysUntilDue}d`, tone: 'red' };
  if (daysUntilDue === 0) return { text: 'Due today', tone: 'red' };
  if (daysUntilDue <= 7) return { text: `Due in ${daysUntilDue}d`, tone: 'amber' };
  return { text: `Due in ${daysUntilDue}d`, tone: 'green' };
};

// Parse a free-text "days before due" list ("3, 0 7") into the normalized
// form the API expects: unique ints in 0..60, sorted largest-first, capped at
// 6. Mirrors validate_reminder_settings in the backend so the UI never submits
// something the server will reject. Invalid tokens are dropped (forgiving).
export const parseDaysBefore = (input) => {
  const tokens = String(input ?? '')
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t !== '');
  const valid = [];
  for (const t of tokens) {
    if (!/^\d+$/.test(t)) continue; // non-negative ints only
    const n = Number(t);
    if (n < 0 || n > 60) continue;
    valid.push(n);
  }
  return [...new Set(valid)].sort((a, b) => b - a).slice(0, 6);
};

// Render a days_before array back into the text-input form.
export const formatDaysBefore = (arr) => (Array.isArray(arr) ? arr : []).join(', ');

const TONE = {
  red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  gray: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
};

const inputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 ' +
  'px-2 py-1 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500';

const DEFAULT_REMINDER = { enabled: true, days_before: [3, 0], time: '09:00' };

// Build the editor's initial draft from a statement's current server values.
const draftFrom = (s) => {
  const r = s.reminder_settings || DEFAULT_REMINDER;
  return {
    due_date: s.due_date ? String(s.due_date).slice(0, 10) : '',
    statement_balance: s.statement_balance ?? '',
    minimum_payment: s.minimum_payment ?? '',
    enabled: r.enabled !== false,
    days_before: formatDaysBefore(r.days_before),
    time: r.time || '09:00',
  };
};

/**
 * A single statement row: a read view with a "Mark Paid" action + an "Edit"
 * toggle that expands an inline editor for the due date, amount, and per-
 * statement reminder settings. Holds its own draft/edit state so typing in one
 * row never re-renders the whole list.
 */
const StatementRow = ({ statement: s, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => draftFrom(s));
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  const status = statusFor(s.days_until_due);
  const reminder = s.reminder_settings || DEFAULT_REMINDER;

  const startEdit = () => {
    setDraft(draftFrom(s)); // reset from latest server state each time
    setError(null);
    setEditing(true);
  };

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        // due_date + statement_balance are NOT NULL in the DB — only send them
        // when present so a blank field never clobbers the stored value.
        reminder_settings: {
          enabled: draft.enabled,
          days_before: parseDaysBefore(draft.days_before),
          time: draft.time || '09:00',
        },
      };
      if (draft.due_date) payload.due_date = draft.due_date;
      if (draft.statement_balance !== '' && draft.statement_balance != null) {
        payload.statement_balance = Number(draft.statement_balance);
      }
      // minimum_payment is nullable — a blank field clears it.
      payload.minimum_payment =
        draft.minimum_payment === '' || draft.minimum_payment == null
          ? null
          : Number(draft.minimum_payment);

      await apiService.updateStatement(s.id, payload);
      setEditing(false);
      await onSaved();
    } catch (e) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async () => {
    setPaying(true);
    setError(null);
    try {
      await apiService.markStatementPaid(s.id);
      await onSaved();
    } catch (e) {
      setError(e?.message || 'Failed to mark paid');
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white truncate">
              {s.account_name || 'Account'}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TONE[status.tone]}`}>
              {status.text}
            </span>
          </div>
          <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-900 dark:text-white">
              {formatCurrency(s.statement_balance)}
            </span>
            {s.minimum_payment ? <span> · min {formatCurrency(s.minimum_payment)}</span> : null}
            {s.due_date ? <span> · due {formatDate(s.due_date)}</span> : null}
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            {reminder.enabled !== false ? (
              <>
                <Bell className="h-3 w-3" />
                <span>
                  {(reminder.days_before && reminder.days_before.length
                    ? reminder.days_before.map((d) => (d === 0 ? 'due day' : `${d}d`)).join(', ')
                    : 'no offsets')}{' '}
                  · {reminder.time || '09:00'}
                </span>
              </>
            ) : (
              <>
                <BellOff className="h-3 w-3" />
                <span>Reminders off</span>
              </>
            )}
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2">
          <button
            type="button"
            onClick={editing ? () => setEditing(false) : startEdit}
            className="inline-flex items-center px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            aria-label={editing ? 'Close editor' : 'Edit statement'}
          >
            {editing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={markPaid}
            disabled={paying}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-400 bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50"
          >
            <CheckCircle className="h-3 w-3 mr-1" />
            {paying ? 'Saving…' : 'Mark Paid'}
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Due date</span>
              <input
                type="date"
                className={inputClass}
                value={draft.due_date}
                onChange={(e) => set('due_date', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Statement balance</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputClass}
                value={draft.statement_balance}
                onChange={(e) => set('statement_balance', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Minimum payment</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputClass}
                value={draft.minimum_payment}
                onChange={(e) => set('minimum_payment', e.target.value)}
                placeholder="none"
              />
            </label>
          </div>

          <div className="rounded-md bg-gray-50 dark:bg-gray-700/40 p-3 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                checked={draft.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Pay reminders on
              </span>
            </label>

            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${draft.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Days before due
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  className={inputClass}
                  value={draft.days_before}
                  onChange={(e) => set('days_before', e.target.value)}
                  placeholder="e.g. 3, 0"
                />
                <span className="mt-0.5 block text-[11px] text-gray-400">
                  comma-separated · 0 = due day · up to 6
                </span>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Time of day</span>
                <input
                  type="time"
                  className={inputClass}
                  value={draft.time}
                  onChange={(e) => set('time', e.target.value)}
                />
              </label>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {!editing && error && (
        <div className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</div>
      )}
    </div>
  );
};

/**
 * Cross-account list of unpaid statements ordered by due date — "what's due
 * when". Self-contained: fetches its own data so it doesn't couple to the
 * dashboard summary. Mirrors FiGuru's Upcoming tab. Each row is editable
 * (due date, amount, per-statement reminder settings).
 */
const UpcomingPayments = () => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getStatements({ unpaid_only: true, limit: 200 });
      const rows = Array.isArray(data) ? data : [];
      rows.sort((a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
      setStatements(rows);
    } catch (e) {
      setError(e?.message || 'Failed to load statements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <Calendar className="h-5 w-5 mr-2" />
          Upcoming Payments
        </h2>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          aria-label="Refresh upcoming payments"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 dark:border-green-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      ) : statements.length === 0 ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center">
          <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-300">Nothing due — all statements are paid 🎉</p>
        </div>
      ) : (
        <div className="space-y-3">
          {statements.map((s) => (
            <StatementRow key={s.id} statement={s} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default UpcomingPayments;
